import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import type { Result, Session } from '../types'
import { ApiError } from '../types'
import { Icon } from '../components/Icon'
import { Bar } from '../components/ui'
import { Banner, Between, Label, Note, Screen, Shell } from '../components/shell'
import { Md, MdInline } from '../components/md'

/* ------------------------------------------------------------------ đồng hồ */

/** Đồng hồ đếm ngược chạy ở CLIENT, hiệu chỉnh theo giờ server.
 *
 *  Ba điều phải làm đúng, nếu không thì đồng hồ nói dối:
 *
 *  1. HIỆU CHỈNH THEO SERVER. Server tính `seconds_left` từ `now() -
 *     started_at` của Postgres. Máy người dùng có thể lệch — đo được
 *     2,8 giây giữa máy app container và Postgres trên cùng máy, và lệch
 *     hàng giờ nếu máy người dùng đặt sai giờ. Client chỉ dùng đồng hồ
 *     máy làm nền, rồi cộng bù.
 *
 *  2. ĐỒNG BỘ LẠI ĐỊNH KỲ. Đếm thuần ở client rẻ nhưng SAI DẦN: tab bị
 *     đóng, máy ngủ, người dùng để máy ngoài 5 phút — sai hàng phút.
 *     Cứ 60 giây hỏi lại server một lần.
 *
 *  3. ĐỒNG BỘ LẠI KHI QUAY VỀ TỪ NỀN. `setInterval` bị dừng HOÀN TOÀN khi
 *     tab ẩn. Người dùng bỏ 5 phút rồi quay lại sẽ thấy đồng hồ vẫn chạy
 *     đều trong khi thực tế đã hết giờ. Đây là lỗi dễ gặp nhất và cũng
 *     dễ sửa nhất — chỉ cần nghe `visibilitychange`.
 */
function useCountdown(session: Session | null) {
  const [left, setLeft] = useState(-1)
  const sessionId = session?.id

  const sync = useCallback(async () => {
    if (!sessionId) return
    try {
      const s = await api.getSession(sessionId)
      setLeft(s.seconds_left)
    } catch {
      // Không đồng bộ được thì giữ số đang đếm. Tốt hơn là đặt về 0 rồi báo
      // hết giờ — người dùng còn giữ được bài đang làm.
    }
  }, [sessionId])

  useEffect(() => {
    setLeft(session ? session.seconds_left : -1)
  }, [session])

  // -1 = không giới hạn thời gian, không đồng hồ nào cả.
  const timed = left >= 0

  useEffect(() => {
    if (!timed) return
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [timed])

  useEffect(() => {
    if (!timed) return
    const t = setInterval(sync, 60_000)
    return () => clearInterval(t)
  }, [sync, timed])

  useEffect(() => {
    if (!timed) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [sync, timed])

  return { left }
}

function fmt(sec: number): string {
  const s = Math.max(0, sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/* ------------------------------------------------------------------ làm bài */

export function Practice({
  session,
  onDone,
  onExit,
}: {
  session: Session
  onDone: (r: Result) => void
  onExit: () => void
}) {
  const [cur, setCur] = useState(0)
  // Đáp án đã chọn theo chỉ số câu. Giữ ở client để tô đậm ô đã bấm, và để
  // gửi lên server ngay khi chọn (xem `choose`).
  const [picked, setPicked] = useState<Record<number, string>>({})
  const [sent, setSent] = useState<Record<number, boolean>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [showGrid, setShowGrid] = useState(false)
  const questionStart = useRef(Date.now())
  // Mốc của lần chọn gần nhất, để gửi thời gian theo ĐỘ DỐI thay vì tổng.
  const lastPickAt = useRef(Date.now())

  const { left } = useCountdown(session)
  const timed = left >= 0
  const limit = session.time_limit_sec ?? 1
  const q = session.questions[cur]

  /** Chuyển câu: reset hai mốc thời gian.
   *
   *  Trước đây có ba chỗ gọi `setCur` riêng và KHÔNG chỗ nào reset
   *  `questionStart` — nên `time_spent_sec` của mọi câu đều là "từ lúc mở app
   *  đến lúc bấm", cộng dồn thành một con số vô nghĩa. Gom về một chỗ thì không
   *  thể sót. */
  function goTo(i: number) {
    setCur(i)
    questionStart.current = Date.now()
    lastPickAt.current = Date.now()
  }

  const submitted = useRef(false)
  useEffect(() => {
    if (!timed || left !== 0 || submitted.current) return
    submitted.current = true
    void doSubmit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, timed])

  // Gửi đáp án về server ngay khi chọn.
  //
  // Vì sao gửi ngay mà không để đến lúc nộp: học viên luyện offline rồi mở
  // app thì đáp án phải còn. Gửi ngay là cách duy nhất giữ được dữ liệu khi
  // mất mạng giữa chừng — chấp nhận thất lạng là gửi hụt và mất đáp án câu
  // đó, chứ không phải mất cả bài.
  async function choose(key: string) {
    // KHÔNG chặn vì `sent[cur]` — được chọn lại. Bấm nhầm A rồi thấy B đúng hơn
    // thì phải sửa được, ngay tại chỗ. Server giờ `ON CONFLICT DO UPDATE` nên
    // đáp án sau cùng thắng.
    if (busy) return
    setPicked((p) => ({ ...p, [cur]: key }))
    setErr('')
    setBusy(true)
    // Gửi khoảng thời gian TỪ LẦN CHỌN TRƯỚC, không phải từ lúc mở câu hỏi:
    // server cộng dồn mỗi lần gửi, nên gửi tổng tích luỹ sẽ tính trùng (chọn
    // lúc 5s rồi 20s thì nhận 5+20=25 thay vì 20).
    const now = Date.now()
    const spent = Math.max(0, Math.round((now - lastPickAt.current) / 1000))
    lastPickAt.current = now
    try {
      await api.answer(session.id, q.session_question_id, key, spent)
      setSent((s) => ({ ...s, [cur]: true }))
    } catch (e) {
      const a = e as ApiError
      // Mất mạng là chuyện bình thường khi luyện di động. KHÔNG báo đỏ — đáp
      // án vẫn giữ ở client. Chỉ báo nếu là lỗi chắc chắn không tự khỏi.
      if (a.code === 'already_submitted') {
        setErr('Bài đã nộp nên không sửa đáp án được nữa.')
      } else if (a.code === 'offline') {
        setErr('Mất mạng — đáp án đã lưu trên máy, sẽ gửi lại khi có mạng.')
      } else {
        setErr(a.detail || 'Không lưu được đáp án.')
      }
    } finally {
      setBusy(false)
    }
  }

  /** Hỏi xác nhận trước khi nộp nếu còn câu bỏ trống.
   *
   *  Nộp là một-NHẤT. Server chặn sửa đáp án sau khi nộp (không có đường mở lại),
   *  nên bấm nhầm là mất luôn những câu chưa làm — mà lúc đó người học vẫn còn
   *  bấm "Tiếp" được và tưởng không sao.
   *
   *  Đếm theo `picked` (đáp án đang chọn ở client) chứ không theo `sent`:
   *  mất mạng thì `sent` thiếu dù người học đã bấm, và hỏi xác nhận cây bốn câu
   *  "bỏ trống" là hỏi vô nghĩa. */
  function askSubmit() {
    const n = session.questions.length
    const done = session.questions.filter((_, i) => picked[i] !== undefined).length
    if (done === n) {
      void doSubmit()
      return
    }
    const blank = n - done
    // Chỉ hỏi khi CÒN câu trống. Đã làm hết thì nộp thẳng, hỏi thừa gây khó chịu.
    const ok = window.confirm(
      `Còn ${blank} câu chưa trả lời.\n\n` +
        `Nộp bài thì những câu đó tính sai, và không sửa lại được. ` +
        `Chắc chắn nộp?`,
    )
    if (ok) void doSubmit()
  }

  async function doSubmit() {
    if (busy) return
    setBusy(true)
    setErr('')
    try {
      onDone(await api.submit(session.id))
    } catch (e) {
      setErr((e as ApiError).detail || 'Không nộp được bài.')
      setBusy(false)
    }
  }

  const saved = Object.values(sent).filter(Boolean).length
  // Cảnh báo khi còn ít thời gian. 60 giây đủ sớm để học viên quyết định
  // nộp sớm thay vì bị cắt.
  const warn = timed && left > 0 && left <= 60
  const last = cur === session.questions.length - 1

  return (
    <Shell>
      <Screen>
        <Between className="mb-2.5">
          {timed ? (
            <span
              className={
                'flex items-center gap-1.5 font-mono text-xl font-semibold ' +
                (warn ? 'text-bad' : 'text-ink')
              }
              role="timer"
              // Chỉ đọc to khi sắp hết giờ — đọc liên tục mỗi giây làm
              // màn đọc bị ngắt quãng.
              aria-live={left <= 10 ? 'assertive' : 'off'}
            >
              <Icon name="clock" size={20} />
              {fmt(left)}
            </span>
          ) : (
            <span />
          )}
          <span className="font-mono text-sm text-ink-faint">
            {cur + 1}/{session.questions.length}
            {saved < session.questions.length && (
              // Số câu đã gửi lên server. Người học cần biết bài đang lưu
              // được đến đâu — nếu không, mất mạng giữa chừng là mất đáp án
              // mà họ không hề biết.
              <span className="ml-2 text-good">{saved} đã lưu</span>
            )}
          </span>
        </Between>

        {timed && (
          <Bar percent={(left / limit) * 100} tone={warn ? 'bg-bad' : 'bg-accent'} className="mb-3" />
        )}

        {warn && (
          <Banner>
            Còn {fmt(left)}. Bài vẫn được chấm đầy đủ khi nộp muộn, nhưng sẽ bị ghi nhận là nộp trễ
            hạn.
          </Banner>
        )}
        {err && <Banner>{err}</Banner>}

        <Between className="mb-1.5">
          {/* Ôn luyện: 1 câu = 1 đề mục nên chip tên đề mục có ích.
              Thi thử: câu trộn ngẫu nhiên mọi đề mục, chip đổi mỗi câu chỉ
              gây rối mắt. Ở chế độ thi chỉ để nút mở lưới câu. */}
          {session.mode === 'timed' ? (
            <span className="text-sm font-semibold text-ink-soft">Thi thử</span>
          ) : (
            <span className="chip">{q.topic_name || q.topic}</span>
          )}
          <button
            onClick={() => setShowGrid((v) => !v)}
            aria-label="Danh sách câu"
            aria-expanded={showGrid}
            className="rounded-lg p-1.5 text-ink-soft hover:bg-canvas-2"
          >
            <Icon name="chart" size={20} />
          </button>
        </Between>

        {showGrid && (
          <div className="card mb-3">
            <div className="grid grid-cols-8 gap-1.5">
              {session.questions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    goTo(i)
                    setShowGrid(false)
                  }}
                  className={
                    'aspect-square cursor-pointer rounded-lg border font-mono text-xs transition-colors ' +
                    (sent[i] ? 'border-good bg-good-soft text-good ' : 'border-ink/15 text-ink-soft ') +
                    (i === cur ? 'border-2 border-accent font-bold text-accent' : '')
                  }
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tình huống: chữ nhỏ mờ như trước, nhưng phải render markdown vì
            nội dung có nhiều đoạn (Bệnh sử / Tiền sử / Khám / Xét nghiệm). */}
        {q.situation && <Md text={q.situation} className="md-note mb-1.5" />}
        <h2 className="mb-4 font-sans text-base font-semibold">
          <MdInline text={q.question} />
        </h2>

        {q.options.map((o) => (
          <button
            key={o.key}
            onClick={() => choose(o.key)}
            // Chỉ khoá lúc đang gửi, để bấm liên tục không dồn request. KHÔNG
            // khoá sau khi đã chọn — bấm nhầm thì phải chọn lại được.
            disabled={busy}
            className={
              'opt px-2.5 py-2 ' +
              (picked[cur] === o.key ? 'opt-picked' : 'hover:bg-canvas-2')
            }
          >
            {/* Nhãn hiển thị (A, B, C, D theo thứ tự đã trộn), KHÔNG phải `o.key`
                — key gốc trong DB, hiện ra sẽ thành C, D, A, B. */}
            <span className="w-4 shrink-0 font-mono text-sm font-semibold text-ink-soft uppercase">
              {o.label}
            </span>
            <span>
              <MdInline text={o.text} />
            </span>
          </button>
        ))}

        <div className="flex-1" />

        <div className="mt-4 mb-1 flex gap-2">
          <button onClick={onExit} disabled={busy} className="btn btn-ghost w-auto px-3">
            Thoát
          </button>
          {cur > 0 && (
            <button onClick={() => goTo(cur - 1)} className="btn btn-ghost w-auto px-3">
              Trước
            </button>
          )}
          {last ? (
            <button onClick={askSubmit} disabled={busy} className="btn btn-gold">
              {busy ? 'Đang chấm…' : 'Nộp bài'}
            </button>
          ) : (
            <button onClick={() => goTo(cur + 1)} className="btn btn-primary">
              Tiếp
              <Icon name="arrow" size={18} />
            </button>
          )}
        </div>

        {/* NỘP BÀI PHẢI CÓ Ở MỌI CÂU, không chỉ câu cuối.
            Trước đây nút nộp chỉ hiện khi `last` — nghĩa là muốn nộp sớm thì
            phải bấm "Tiếp" hết 20 câu. Thi thử có đồng hồ đếm ngược nên hết
            giờ giữa chừng là chuyện thường, và lúc đó người học không còn cách
            nào chủ động kết thúc bài.
            Ở câu cuối thì nút chính đã là "Nộp bài" nên không hiện thêm ở đây —
            tránh hai nút cùng một việc cạnh nhau. */}
        {!last && (
          <button onClick={askSubmit} disabled={busy} className="btn btn-ghost">
            Nộp bài sớm
          </button>
        )}
      </Screen>
    </Shell>
  )
}

/* ------------------------------------------------------------------ kết quả */

export function ResultView({
  r,
  onAgain,
  onHome,
}: {
  r: Result
  onAgain: () => void
  onHome: () => void
}) {
  const wrong = r.items.filter((i) => !i.correct)
  const pct = Math.round(r.percent)

  return (
    <Shell>
      <Screen>
        {r.submitted_late && (
          <Banner tone="warn">
            Bài nộp sau {r.buffer_sec} giây đệm. Bạn vẫn nhận đủ điểm, nhưng bài này được tính
            là nộp trễ hạn.
          </Banner>
        )}

        {/* Padding ghi đè `.card` là CỐ Ý: thẻ điểm cần khoảng thở dọc lớn
            hơn, và rộng hơn vì chữ "8/10" căn giữa. */}
        <div className="card mb-3 px-4 py-6 text-center">
          <div
            className={
              'font-mono text-4xl font-semibold ' +
              (pct >= 80 ? 'text-good' : pct >= 50 ? 'text-ink' : 'text-bad')
            }
          >
            {pct}%
          </div>
          <Note className="mt-1">
            {r.score_correct}/{r.score_total} câu đúng
          </Note>
        </div>

        <Label>Cần xem lại ({wrong.length})</Label>
        {wrong.length === 0 && (
          <Note className="mt-1.5 mb-3">Không có câu sai. Tốt lắm.</Note>
        )}

        <div className="mt-1.5 mb-4 space-y-2">
          {wrong.map((it) => (
            <div key={it.code} className="card">
              <Between className="mb-1">
                <span className="font-mono text-xs text-ink-faint">{it.code}</span>
                <span className="chip bg-bad-soft text-bad">
                  {it.answered ? `sai ${it.selected}` : 'bỏ trống'}
                </span>
              </Between>
              {!it.answered && <Note className="mb-1">Bạn không trả lời câu này.</Note>}
              {/* Giải thích dài, có `**đậm**` ở đầu mỗi mục và gạch đầu dòng
                  `•` liệt kê phương án — không render markdown thì người học
                  thấy đúng dấu `**` và mất thụt lề danh sách. */}
              {it.explanation && <Md text={it.explanation} className="md-sm" />}
              {it.takeaway && (
                <Md text={it.takeaway} className="md-sm md-soft mt-1" />
              )}
            </div>
          ))}
        </div>

        <button onClick={onAgain} className="btn btn-primary mb-2">
          Luyện tiếp
          <Icon name="arrow" size={18} />
        </button>
        <button onClick={onHome} className="btn btn-ghost">
          Về trang chủ
        </button>
      </Screen>
    </Shell>
  )
}


